import { createColumnHelper } from "@tanstack/react-table";
import Tabs, { ITab } from "@/components/Tabs";
import { HeaderTextBlock, TypographyContainer } from "@/components/Typography";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/Badge";
import { formatDate } from "@/utils/helpers";
import { Table } from "@/components/Table";
import { useGetContactsTable } from "@/requests/hooks/collect";
import { Button } from "@/components/Button";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";

interface IProps {
  channel: "EMAIL" | "SMS";
}

const AuditLogs = ({ channel }: IProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const tabs: ITab[] = [
    {
      path: "?marketable=MARKETABLE",
      title: t("common.auditLogs.tabs.marketable"),
      checkIsActive: () => searchParams.get("marketable") === "MARKETABLE",
    },
    {
      path: "?marketable=NON_MARKETABLE",
      title: t("common.auditLogs.tabs.nonMarketable"),
      checkIsActive: () => searchParams.get("marketable") === "NON_MARKETABLE",
    },
  ];

  const columnHelper = createColumnHelper<IContact>();

  const columns = [
    columnHelper.accessor("identifier", {
      header: t(`common.auditLogs.table.columns.${channel}`),
    }),
    columnHelper.accessor("name", {
      header: t("common.auditLogs.table.columns.name"),
      cell: info =>
        !!info?.getValue()
          ? info?.getValue()
          : t("common.auditLogs.table.row.unknown"),
    }),
    columnHelper.accessor("marketable", {
      header: t("common.auditLogs.table.columns.status.title"),
      cell: info => {
        const isMarketable = info.getValue();

        return (
          <Badge variant={isMarketable ? "success" : "error"}>
            {t(
              `common.auditLogs.table.columns.status.${isMarketable ? "marketable" : "nonMarketable"}`,
            )}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: t("common.auditLogs.table.columns.rationale"),
      cell: info => t(`common.contacts.rationale.${info.getValue()}`),
    }),
    columnHelper.accessor("lastModifiedAt", {
      header: t("common.auditLogs.table.columns.lastModifiedAt"),
      cell: info => formatDate(info.getValue()),
    }),
    columnHelper.accessor("id", {
      header: t("common.auditLogs.table.columns.details"),
      cell: info => {
        const rowData = info.row.original;

        return (
          <Button
            as="link"
            width="unset"
            to={rowData.id}
            variant="link"
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="text-secondary">
            {t("common.auditLogs.table.detailsButton")}
          </Button>
        );
      },
    }),
  ];

  return (
    <>
      <TypographyContainer>
        <HeaderTextBlock
          align="left"
          featureText={t("common.auditLogs.header.feature")}
          headingText={t("common.auditLogs.header.heading")}
          descriptionText={t("common.auditLogs.header.description")}
        />
      </TypographyContainer>

      <div className="my-40">
        <Tabs tabs={tabs} />
      </div>

      <Table
        columns={columns}
        queryKey={["collect", "auditLogs", channel]}
        queryHook={useGetContactsTable}
        filters={{
          channel: channel.toUpperCase(),
          marketable: searchParams.get("marketable") || "MARKETABLE",
        }}
      />
    </>
  );
};

export default AuditLogs;
